import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
  AfterViewInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import type Quill from 'quill';

export type QuillThemeType = 'snow' | 'bubble';

@Component({
  selector: 'black-rich-text-editor, black-editor',
  templateUrl: './ui-rich-text-editor.component.html',
  standalone: false,
  host: {
    class: 'block',
    ngSkipHydration: 'true',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiRichTextEditorComponent),
      multi: true,
    },
  ],
})
export class UiRichTextEditorComponent
  implements AfterViewInit, OnDestroy, ControlValueAccessor
{
  private platformId = inject(PLATFORM_ID);

  @ViewChild('editorContainer', { static: false })
  editorContainer!: ElementRef<HTMLDivElement>;

  /**
   * Quill theme variant:
   * - 'snow': Standard top toolbar with comprehensive rich text formatting
   * - 'bubble': Sleek floating tooltip toolbar appearing on text selection
   */
  @Input() theme: QuillThemeType = 'snow';
  @Input() placeholder = 'Write something...';
  @Input() readOnly = false;
  @Input() minHeight = '200px';
  @Input() maxHeight?: string;
  @Input() showThemeToggle = false;
  @Input() customModules?: any;

  @Output() contentChange = new EventEmitter<string>();
  @Output() editorCreated = new EventEmitter<Quill>();
  @Output() themeChange = new EventEmitter<QuillThemeType>();

  quillInstance: Quill | null = null;
  private innerValue = '';
  private isUpdating = false;

  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.initQuill();
  }

  ngOnDestroy(): void {
    this.destroyQuill();
  }

  private destroyQuill(): void {
    if (this.quillInstance) {
      this.quillInstance.off('text-change');
      this.quillInstance.off('selection-change');
      this.quillInstance = null;
    }
  }

  async switchTheme(newTheme: QuillThemeType): Promise<void> {
    if (this.theme === newTheme || !isPlatformBrowser(this.platformId)) return;

    if (this.quillInstance) {
      this.innerValue = this.quillInstance.root.innerHTML;
      this.destroyQuill();

      // Clean up previous toolbar and editor DOM
      const wrapper = this.editorContainer?.nativeElement?.parentElement;
      if (wrapper) {
        const toolbar = wrapper.querySelector('.ql-toolbar');
        if (toolbar) toolbar.remove();
      }

      if (this.editorContainer?.nativeElement) {
        this.editorContainer.nativeElement.innerHTML = '';
        this.editorContainer.nativeElement.className = 'black-quill-editor';
      }
    }

    this.theme = newTheme;
    this.themeChange.emit(newTheme);
    await this.initQuill();
  }

  private async initQuill(): Promise<void> {
    if (
      !isPlatformBrowser(this.platformId) ||
      !this.editorContainer?.nativeElement
    )
      return;

    const defaultToolbar =
      this.theme === 'snow'
        ? [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['blockquote', 'code-block'],
            ['link', 'image'],
            ['clean'],
          ]
        : [
            ['bold', 'italic', 'underline', 'strike'],
            [{ header: [1, 2, false] }],
            ['blockquote', 'link'],
            ['clean'],
          ];

    const modules = this.customModules || {
      toolbar: defaultToolbar,
    };

    const { default: Quill } = await import('quill');

    this.quillInstance = new Quill(this.editorContainer.nativeElement, {
      theme: this.theme,
      placeholder: this.placeholder,
      readOnly: this.readOnly,
      modules,
    });

    if (this.innerValue) {
      this.quillInstance.root.innerHTML = this.innerValue;
    }

    this.quillInstance.on('text-change', () => {
      if (this.isUpdating || !this.quillInstance) return;

      const html = this.quillInstance.root.innerHTML;
      const text = this.quillInstance.getText().trim();
      const isEmpty = text.length === 0 && !html.includes('<img');
      const val = isEmpty ? '' : html;

      this.innerValue = val;
      this.onChange(val);
      this.contentChange.emit(val);
    });

    this.quillInstance.on('selection-change', (range) => {
      if (!range) {
        this.onTouched();
      }
    });

    this.editorCreated.emit(this.quillInstance);
  }

  // ── ControlValueAccessor Implementation ──────────────────────────────────────

  writeValue(value: any): void {
    const stringVal = value == null ? '' : String(value);
    this.innerValue = stringVal;

    if (this.quillInstance && this.quillInstance.root) {
      if (this.quillInstance.root.innerHTML !== stringVal) {
        this.isUpdating = true;
        this.quillInstance.root.innerHTML = stringVal;
        this.isUpdating = false;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.readOnly = isDisabled;
    if (this.quillInstance) {
      this.quillInstance.enable(!isDisabled);
    }
  }
}
